/**
 * @created     2026-03-26
 * @description Overlay modal for document upload (Nachweis/Beleg).
 *              Used by DEFER_INPUT, SETTLE_INPUT, INFO_DEFECT_PROOF, INFO_PAID_PROOF nodes.
 *              Depends on: ChatAction modalId 'document_upload' from bot response
 */
import {
  Component,
  EventEmitter,
  Input,
  Output,
  ChangeDetectionStrategy,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpEvent, HttpEventType } from '@angular/common/http';
import { isDevServer } from '../../../core/utils/dev-server.util';
import {
  DOCUMENT_UPLOAD_ACCEPT,
  DOCUMENT_UPLOAD_MAX_FILES,
  DocumentUploadApiResponse,
  DocumentUploadService,
  selectDocumentUploadFiles,
} from '../../../core/services/document-upload.service';
import { I18nService } from '../../../core/services/i18n.service';
import { TranslatePipe } from '../../pipes/translate.pipe';
import {
  documentUploadCommentCodePointLength,
  DOCUMENT_UPLOAD_COMMENT_MAX_CODE_POINTS,
  isDocumentUploadCommentTooLong,
  isValidDocumentUploadComment,
  normalizeDocumentUploadComment,
} from '../../utils/form-validators';

interface UploadFileState {
  file: File;
  name: string;
  size: number;
  progress: number;
  status: 'pending' | 'uploading' | 'done' | 'error';
  error?: string;
}

export interface DocumentUploadCompletePayload {
  mode: 'sftp_upload';
  filenames: string[];
  file_count: number;
  category: string;
  reason: string;
  comment: string;
}

@Component({
  selector: 'app-document-upload-modal',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  templateUrl: './document-upload-modal.component.html',
  styleUrl: './document-upload-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentUploadModalComponent {
  private readonly documentUploadService = inject(DocumentUploadService);
  private readonly i18n = inject(I18nService);

  @Input() modalNonce?: string;

  /** Emits when the modal should be closed without uploading */
  @Output() close = new EventEmitter<void>();

  /** Emits when the real upload completed */
  @Output() uploadComplete = new EventEmitter<DocumentUploadCompletePayload>();

  readonly showDevHelper = isDevServer();
  readonly isDragOver = signal(false);
  readonly files = signal<UploadFileState[]>([]);
  readonly category = signal('proof');
  readonly reason = signal('');
  readonly comment = signal('');
  readonly errorMessage = signal<string | null>(null);
  readonly isUploading = signal(false);
  readonly uploadDone = signal(false);
  readonly maxFiles = DOCUMENT_UPLOAD_MAX_FILES;
  readonly documentUploadCommentMaxLength = DOCUMENT_UPLOAD_COMMENT_MAX_CODE_POINTS;
  readonly acceptedTypes = DOCUMENT_UPLOAD_ACCEPT;

  onClose(): void {
    this.close.emit();
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver.set(true);
  }

  onDragLeave(): void {
    this.isDragOver.set(false);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver.set(false);
    this.addFiles(event.dataTransfer?.files);
  }

  onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.addFiles(input.files);
    input.value = '';
  }

  selectedFilesStatus(): string {
    const count = this.files().length;
    if (count === 0) {
      return this.i18n.t('documentUploadModal.noFilesSelected');
    }

    return this.i18n.t('documentUploadModal.selectedFiles', { count });
  }

  onUpload(): void {
    if (this.files().length === 0 || this.isUploading()) return;

    const reason = this.reason().trim();
    if (reason === '') {
      this.errorMessage.set(this.i18n.t('documentUploadModal.reasonRequired'));
      return;
    }

    const comment = normalizeDocumentUploadComment(this.comment());
    if (!isValidDocumentUploadComment(comment)) {
      this.errorMessage.set(this.i18n.t(
        isDocumentUploadCommentTooLong(comment)
          ? 'documentUploadModal.commentTooLong'
          : 'documentUploadModal.commentRequired'
      ));
      return;
    }

    this.errorMessage.set(null);
    this.uploadDone.set(false);
    this.isUploading.set(true);
    this.files.update((files) => files.map((file) => ({ ...file, status: 'uploading', progress: 0, error: undefined })));

    const uploadFiles = this.files().map((file) => file.file);
    const fileSizes = uploadFiles.map((file) => Math.max(file.size, 1));
    const totalBytes = fileSizes.reduce((sum, size) => sum + size, 0);

    this.documentUploadService.upload(uploadFiles, {
      category: this.category(),
      reason,
      comment,
      modalNonce: this.modalNonce,
    }).subscribe({
      next: (event) => this.handleUploadEvent(event, fileSizes, totalBytes),
      error: (error) => this.failUpload(error?.error?.message ?? error?.error?.error ?? this.i18n.t('documentUploadModal.uploadFailed')),
    });
  }

  onSimulateUpload(): void {
    if (this.isUploading()) return;
    const file = new File(["%PDF-1.4\n% Test upload\n%%EOF\n"], 'test-upload.pdf', { type: 'application/pdf' });
    this.files.set([this.toUploadFileState(file)]);
    this.category.set('proof');
    this.reason.set('Zahlungsnachweis');
    this.comment.set('TESTDATEN Upload.');
    this.errorMessage.set(null);
  }

  onCategoryChange(value: string): void {
    this.category.set(value);
  }

  onReasonChange(value: string): void {
    this.reason.set(value);
  }

  onCommentChange(value: string): void {
    this.comment.set(value);
  }

  hasValidComment(): boolean {
    return isValidDocumentUploadComment(this.comment());
  }

  documentUploadCommentLength(): number {
    return documentUploadCommentCodePointLength(this.comment());
  }

  isDocumentUploadCommentTooLong(): boolean {
    return isDocumentUploadCommentTooLong(this.comment());
  }

  removeFile(index: number): void {
    if (this.isUploading()) return;
    this.files.update((files) => files.filter((_, currentIndex) => currentIndex !== index));
  }

  private addFiles(fileList: FileList | null | undefined): void {
    if (!fileList || fileList.length === 0) return;

    const { files, error } = selectDocumentUploadFiles(
      this.files().map((selected) => selected.file),
      Array.from(fileList),
      (key) => this.i18n.t(key),
    );
    this.files.update((current) => [...current, ...files.map((file) => this.toUploadFileState(file))]);
    this.errorMessage.set(error);
    this.uploadDone.set(false);
  }

  private toUploadFileState(file: File): UploadFileState {
    return {
      file,
      name: file.name,
      size: file.size,
      progress: 0,
      status: 'pending',
    };
  }

  private handleUploadEvent(event: HttpEvent<DocumentUploadApiResponse>, fileSizes: number[], totalBytes: number): void {
    if (event.type === HttpEventType.UploadProgress) {
      const loaded = Math.min(event.loaded, totalBytes);
      let previousBytes = 0;
      this.files.update((files) => files.map((file, index) => {
        const fileSize = fileSizes[index] ?? 1;
        const progress = Math.max(0, Math.min(100, Math.round(((loaded - previousBytes) / fileSize) * 100)));
        previousBytes += fileSize;
        return { ...file, progress };
      }));
      return;
    }

    if (event.type !== HttpEventType.Response) {
      return;
    }

    const uploadedNames = event.body?.data.uploads.map((upload) => upload.original_name) ?? this.files().map((file) => file.name);
    this.files.update((files) => files.map((file) => ({ ...file, status: 'done', progress: 100 })));
    this.isUploading.set(false);
    this.uploadDone.set(true);
    this.uploadComplete.emit({
      mode: 'sftp_upload',
      filenames: uploadedNames,
      file_count: uploadedNames.length,
      category: this.category(),
      reason: this.reason().trim(),
      comment: normalizeDocumentUploadComment(this.comment()),
    });
  }

  private failUpload(message: string): void {
    this.files.update((files) => files.map((file) => ({ ...file, status: 'error', error: message })));
    this.errorMessage.set(message);
    this.isUploading.set(false);
  }

  onSkip(): void {
    this.close.emit();
  }
}
